/*
  STRYKR — Cart custom elements (patch 20260519)
  ----------------------------------------------
  Reson d'être : le thème Shrine de Strykr utilise `<cart-remove-button>`,
  `<quantity-input>`, `<cart-drawer-items>`, `<cart-items>` et `<cart-note>`
  dans les sections cart-drawer / main-cart-items, mais le bundle `secondary.js`
  livré ne définit AUCUN de ces custom elements (vérifié : 20 customElements
  enregistrés, aucun lié au panier).

  Sans définition, les boutons "supprimer" sont inertes, les +/- quantité
  ne déclenchent rien, le textarea note ne sauvegarde pas.

  Ce script définit minimalement les composants nécessaires en suivant le
  pattern Dawn/Shrine pour rester compatible avec le HTML existant.

  Idempotent : peut être chargé plusieurs fois ou réinjecté après un
  re-render de section. Les custom elements sont définis une seule fois
  via `customElements.get()` guard. Les listeners `connectedCallback` sont
  protégés par un flag `dataset.strykrBound`.
*/
(() => {
  'use strict';

  if (window.__STRYKR_CART_ELEMENTS_20260519__) return;
  window.__STRYKR_CART_ELEMENTS_20260519__ = true;

  const ON_CHANGE_DEBOUNCE = 300;

  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const debounce = (fn, wait) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const sectionUrl = (ids) => {
    const url = new URL(rootPath(), window.location.origin);
    url.searchParams.set('sections', ids.join(','));
    return url.toString();
  };

  const replaceSection = (id, html) => {
    if (!html) return;
    const parser = new DOMParser();
    const nextDoc = parser.parseFromString(html, 'text/html');
    const next = nextDoc.getElementById(`shopify-section-${id}`) || nextDoc.getElementById(id);
    const current = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
    if (current && next) current.innerHTML = next.innerHTML;
  };

  const refreshDrawer = async () => {
    try {
      // PATCH 20260520 BUG A : sauvegarder l'état "active" du drawer AVANT refresh.
      // Le HTML serveur ne sait pas que le drawer était ouvert ; sans cette préservation,
      // le drawer disparaît visuellement après refresh tout en laissant body.overflow-hidden
      // → scroll bloqué.
      const beforeDrawer = document.querySelector('cart-drawer');
      const wasActive = beforeDrawer && beforeDrawer.classList.contains('active');

      const response = await fetch(sectionUrl(['cart-drawer', 'cart-icon-bubble']), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) return;
      const sections = await response.json();
      replaceSection('cart-drawer', sections['cart-drawer']);
      replaceSection('cart-icon-bubble', sections['cart-icon-bubble']);

      // Restaurer l'état "active" sur le nouveau cart-drawer
      const afterDrawer = document.querySelector('cart-drawer');
      if (afterDrawer && wasActive) {
        afterDrawer.classList.add('active');
      }
    } catch (_) { /* silencieux */ }
  };

  const refreshCartCount = async () => {
    try {
      const cart = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      
      // Update badge text
      document.querySelectorAll('[data-cart-count], .cart-count-bubble span, #cart-count').forEach((node) => {
        node.textContent = String(cart.item_count);
      });

      // Special case for cart-icon-bubble if the span doesn't exist yet (0 -> 1)
      const bubbleContainer = document.getElementById('cart-icon-bubble');
      if (bubbleContainer && cart.item_count > 0 && !bubbleContainer.querySelector('.cart-count-bubble')) {
        await refreshDrawer();
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart } }));

      // Si panier vide, marque le drawer en is-empty et débloque le scroll (BUG A)
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        if (cart.item_count === 0) {
          drawer.classList.add('is-empty');
          document.body.classList.remove('overflow-hidden');
        } else {
          drawer.classList.remove('is-empty');
        }
      }
      return cart;
    } catch (_) {
      return null;
    }
  };

  const showItemLoading = (itemRoot) => {
    if (!itemRoot) return;
    const overlay = itemRoot.querySelector('.loading-overlay');
    if (overlay) overlay.classList.remove('hidden');
  };

  // PATCH 20260519: cacher l'overlay loading en cas d'erreur — sinon il reste affiché
  // indéfiniment, donnant l'impression que le bouton est figé.
  const hideItemLoading = (itemRoot) => {
    if (!itemRoot) return;
    const overlay = itemRoot.querySelector('.loading-overlay');
    if (overlay) overlay.classList.add('hidden');
  };

  const showLineError = (line, message) => {
    const err = document.getElementById(`CartDrawer-LineItemError-${line}`) ||
                document.getElementById(`Line-item-error-${line}`);
    if (!err) return;
    const text = err.querySelector('.cart-item__error-text');
    if (text) text.textContent = message || 'Fehler beim Aktualisieren';
  };

  // PATCH 20260519: verrou global pour les updates de panier.
  // Les lignes Shopify sont indexées (line 1, 2, 3...) et leurs indices changent après chaque
  // suppression. Si on traitait deux clics en parallèle (ou même séquentiellement avant le
  // refresh DOM), le 2e clic opérerait sur un line index périmé → suppression de la mauvaise
  // ligne. Le verrou ignore tous les clics tant que l'update + refresh n'est pas terminée.
  let cartIsUpdating = false;

  const cartChange = async (line, quantity) => {
    const response = await fetch(`${rootPath()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
    if (!response.ok) {
      let msg = 'Fehler';
      try { msg = (await response.json()).description || msg; } catch (_) {}
      throw new Error(msg);
    }
    return response.json();
  };

  const isOnCartPage = () => {
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '/cart' || path.endsWith('/cart');
  };

  /* ======================================================================
     cart-items (page /cart) et cart-drawer-items (cart drawer)
     ====================================================================== */
  class CartItemsBase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.strykrBound === 'true') return;
      this.dataset.strykrBound = 'true';

      this.addEventListener(
        'change',
        debounce((event) => {
          const target = event.target;
          if (!target || !target.matches || !target.matches('input[name="updates[]"]')) return;
          const line = target.dataset.index;
          const quantity = target.value;
          if (line == null) return;
          this.updateQuantity(line, quantity);
        }, ON_CHANGE_DEBOUNCE)
      );
    }

    async updateQuantity(line, quantity) {
      // PATCH 20260519: refuser les clics concurrents tant que l'update précédente
      // n'est pas terminée (sinon line index périmé → mauvaise ligne supprimée).
      if (cartIsUpdating) return;
      cartIsUpdating = true;

      const itemRoot =
        document.getElementById(`CartDrawer-Item-${line}`) ||
        document.getElementById(`CartItem-${line}`);
      showItemLoading(itemRoot);

      try {
        await cartChange(line, quantity);

        // Page /cart : reload pour rafraîchir totals + lignes
        if (this.tagName === 'CART-ITEMS' && isOnCartPage()) {
          window.location.reload();
          return;
        }

        // Sinon (cart drawer ou cart-items partiellement utilisé) : re-render le drawer
        await refreshDrawer();
        await refreshCartCount();
      } catch (e) {
        showLineError(line, e && e.message);
        // PATCH 20260519: démasquer l'overlay en cas d'erreur, sinon UI bloquée.
        hideItemLoading(itemRoot);
      } finally {
        cartIsUpdating = false;
      }
    }
  }

  if (!customElements.get('cart-items')) {
    customElements.define('cart-items', CartItemsBase);
  }
  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', class extends CartItemsBase {});
  }

  /* ======================================================================
     cart-remove-button : bouton "supprimer" un item
     ====================================================================== */
  if (!customElements.get('cart-remove-button')) {
    customElements.define(
      'cart-remove-button',
      class extends HTMLElement {
        connectedCallback() {
          if (this.dataset.strykrBound === 'true') return;
          this.dataset.strykrBound = 'true';

          this.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const container = this.closest('cart-items, cart-drawer-items');
            const line = this.dataset.index;
            if (!container || line == null) return;
            if (typeof container.updateQuantity === 'function') {
              container.updateQuantity(line, 0);
            }
          });
        }
      }
    );
  }

  /* ======================================================================
     quantity-input : boutons +/- liés à l'input quantité
     ====================================================================== */
  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class extends HTMLElement {
        connectedCallback() {
          if (this.dataset.strykrBound === 'true') return;
          this.dataset.strykrBound = 'true';

          const input = this.querySelector('input[type="number"]');
          if (!input) return;

          this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              const previous = input.value;
              if (button.name === 'plus') {
                input.stepUp();
              } else {
                input.stepDown();
              }
              if (input.value !== previous) {
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          });
        }
      }
    );
  }

  /* ======================================================================
     cart-note : textarea note de commande (sauvegarde debounced)
     ====================================================================== */
  if (!customElements.get('cart-note')) {
    customElements.define(
      'cart-note',
      class extends HTMLElement {
        connectedCallback() {
          if (this.dataset.strykrBound === 'true') return;
          this.dataset.strykrBound = 'true';

          const textarea = this.querySelector('textarea');
          if (!textarea) return;
          textarea.addEventListener(
            'input',
            debounce(async () => {
              try {
                await fetch(`${rootPath()}cart/update.js`, {
                  method: 'POST',
                  credentials: 'same-origin',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ note: textarea.value })
                });
              } catch (_) { /* silencieux */ }
            }, ON_CHANGE_DEBOUNCE)
          );
        }
      }
    );
  }
})();
